import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";
import { http } from "@/lib/api";
import { avatarColor, initials } from "@/lib/uiHelpers";

export default function TeamChat({ lead, me, onUpdate }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const comments = (lead.transcript || []).filter((m) => m.role === "internal");

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments.length]);

  const post = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const { data } = await http.post(`/leads/${lead.id}/comments`, { text: t });
      onUpdate?.(data);
      setText("");
    } catch (err) {
      toast.error("Failed to post comment");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-t px-6 py-5">
      <div className="text-xs uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5">
        <MessageSquare className="w-3 h-3" /> Team chat · {comments.length}
      </div>

      <div className="space-y-2 max-h-72 overflow-y-auto thin-scroll mb-3 pr-1">
        {comments.length === 0 && (
          <div className="text-xs text-slate-400 italic py-3">No internal comments yet. Start the discussion below.</div>
        )}
        {comments.map((c, i) => {
          const author = c.sent_by_name || c.sent_by || "Team";
          const mine = c.sent_by === me.email;
          const color = avatarColor(author);
          return (
            <div key={c.id || `${c.sent_by}-${c.sent_at || i}`} data-testid={`team-comment-${i}`} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                style={{ background: color.bg, color: color.text }}
                title={author}
              >
                {initials(author)}
              </span>
              <div className={`max-w-[80%] ${mine ? "text-right" : ""}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">
                  {author} · {c.sent_by_role || "staff"} · {new Date(c.sent_at).toLocaleString()}
                </div>
                <div
                  className={`inline-block rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    mine ? "bg-[#0B1E3F] text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}
                >
                  {c.content}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={post} className="flex gap-2">
        <input
          data-testid="team-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a private note for the team…"
          className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0B1E3F]"
        />
        <button
          type="submit"
          data-testid="team-chat-send"
          disabled={sending || !text.trim()}
          className="rounded-lg bg-[#0B1E3F] text-white px-3 py-2 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
        >
          <Send className="w-3 h-3" /> Post
        </button>
      </form>
    </div>
  );
}
